import React, { useState } from 'react';
import { TextField, Button, Box, Typography, Container, Grid, InputAdornment, IconButton, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import api from '../../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';

function CreateAccount() {
    const [showPassword, setShowPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [formData, setFormData] = useState({
        email: '',
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const navigate = useNavigate();

    const handleAccountCreation = async (e) => {
        e.preventDefault();
        const passwordValidationResult = validatePassword(formData.newPassword);
        if (passwordValidationResult !== true) {
            setErrorMessage(passwordValidationResult);
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            setErrorMessage("Passwords do not match");
            return;
        }

        try {
            // First verify the unique ID
            // For account creation, we don't want to send the auth token
            const response = await api.post(`/api/caregiver/finalizeCreateAccount`, formData, {
                headers: {
                    "Authorization": '' // Override the default Authorization header
                }
            });
            if (response.status === 200) {
                navigate('/login');
            }
        } catch (error) {
            console.error('Error creating account:', error);
            // if response status is 409, the email is already in use
            setErrorMessage(error.response?.data?.message || 'Error creating account');
        }
    };

    const validatePassword = (password) => {
        const lengthCheck = /^.{8,30}$/;
        const digitCheck = /\d/;
        const upperCaseCheck = /[A-Z]/;
        const lowerCaseCheck = /[a-z]/;
        const specialCharacterCheck = /[!@#$%^&*(),.?":{}|<>]/;
        let err = '';
        if (!lengthCheck.test(password)) {
            err += 'Password must be between 8 and 30 characters in length. ';
        }
        if (!digitCheck.test(password)) {
            err += 'Password must include at least one digit. ';
        }
        if (!upperCaseCheck.test(password) || !lowerCaseCheck.test(password)) {
            err += 'Password must include both upper and lower case characters. ';
        }
        if (!specialCharacterCheck.test(password)) {
            err += 'Password must include at least one special character.';
        }
        if (!err) {
            return true;  // Return true if all checks pass
        } else {
            return err;
        }
    };

    return (
        <Container maxWidth="xs">
            <Box sx={{ mt: 5 }}>
                <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '90vh', overflow: 'hidden', margin: 0 }}>
                    <Grid item>
                        <Box sx={{ padding: '20px', textAlign: 'center', maxWidth: '300px', margin: '0 auto' }}>
                            <Typography variant="h5" gutterBottom>
                                Finalize Creation
                            </Typography>
                            <TextField
                                label="Email"
                                fullWidth
                                margin="normal"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                            <Box sx={{ position: 'relative' }}>
                                <TextField
                                    label="Temporary Password"
                                    type={showPassword ? 'text' : 'password'}  // Toggle input type based on state
                                    fullWidth
                                    margin="normal"
                                    value={formData.oldPassword}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}  // Toggle showPassword state
                                                    edge="end"
                                                >
                                                    {!showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    onChange={(e) => setFormData({ ...formData, oldPassword: e.target.value })}
                                />
                            </Box>
                            <Box sx={{ position: 'relative' }}>
                                <TextField
                                    label="New Password"
                                    type={showPassword ? 'text' : 'password'}  // Toggle input type based on state
                                    fullWidth
                                    margin="normal"
                                    value={formData.newPassword}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}  // Toggle showPassword state
                                                    edge="end"
                                                >
                                                    {!showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                                />
                                <Tooltip
                                    title={
                                        <div>
                                            <Typography variant="body1">Password Requirements:</Typography>
                                            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '16px' }}>
                                                <li>Password must be between 8 and 30 characters in length.</li>
                                                <li>Password must include at least one digit.</li>
                                                <li>Password must include both upper and lower case characters.</li>
                                                <li>Password must include at least one special character.</li>
                                            </ul>
                                        </div>
                                    }
                                >
                                    <IconButton
                                        color="primary"
                                        sx={{
                                            position: 'absolute',
                                            right: '-50px',
                                            top: '25px',
                                        }}>
                                        <InfoIcon

                                        />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <TextField
                                label="Confirm Password"
                                type={showPassword ? 'text' : 'password'}  // Toggle input type based on state
                                fullWidth
                                margin="normal"
                                value={formData.confirmPassword}
                                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                required
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}  // Toggle showPassword state
                                                edge="end"
                                            >
                                                {!showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {errorMessage && <Typography color="error" sx={{ marginBottom: '16px' }}>{errorMessage}</Typography>}
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAccountCreation}
                                fullWidth
                                sx={{ marginTop: '16px' }}
                            >
                                Create Account
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
}

export default CreateAccount;